// src/App.tsx
import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Typography,
  Spin,
  Card,
  Button,
  Input,
  message,
  ConfigProvider,
  Tag,
  Grid,
  FloatButton,
} from "antd";
import {
  BookOutlined,
  HistoryOutlined,
  ReadOutlined,
  ThunderboltOutlined,
  FlagOutlined,
  RobotOutlined,
  SendOutlined,
  CrownFilled,
  FireOutlined,
  AppstoreOutlined,
  MenuOutlined,
  CloseOutlined,
  LoadingOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

// Import Service và Component bàn cờ
import { fetchAIChessContent } from "./services/aiService";
import XiangqiBoard from "./components/XiangqiBoard";

import type { MenuProps } from "antd";

const { Header, Content, Sider, Footer } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

// --- DỮ LIỆU FEN & PROMPT (DATA STORE) ---
// (Giữ nguyên phần Data Store cũ không đổi)
interface TopicData {
  label: string;
  prompt: string;
  fen: string;
  tag?: string;
}

const OPENING_DATA: Record<string, TopicData> = {
  open_central: {
    label: "Pháo Đầu (Central Cannon)",
    prompt:
      "Phân tích khai cuộc Pháo Đầu (Trung Pháo). Ưu nhược điểm khi đi Pháo 2 bình 5 nước đầu tiên?",
    fen: "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C2C4/9/RNBAKABNR b - - 0 1",
  },
  open_screen_horse: {
    label: "Bình Phong Mã (Screen Horses)",
    prompt:
      "Phân tích thế trận Bình Phong Mã. Tại sao đây là thế trận phòng thủ mạnh nhất chống lại Pháo Đầu?",
    fen: "r1bakab1r/9/1cn3nc1/p1p1p1p1p/9/9/P1P1P1P1P/2N1C2C1/9/R1BAKABNR r - - 0 3",
  },
  open_elephant: {
    label: "Phi Tượng Cuộc (Elephant)",
    prompt:
      "Phân tích khai cuộc Phi Tượng (Tượng 3 tấn 5). Lối đánh này phù hợp với phong cách nào?",
    fen: "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C2B2C1/9/RNBAKA1NR b - - 0 1",
  },
  open_pawn: {
    label: "Tiên Nhân Chỉ Lộ (Angel Guide)",
    prompt:
      "Phân tích nước đi 'Tiên Nhân Chỉ Lộ' (Binh 3 tấn 1 hoặc Binh 7 tấn 1). Mục đích thăm dò của nó là gì?",
    fen: "rnbakabnr/9/1c5c1/p1p1p1p1p/9/6P2/P1P1P3P/1C5C1/9/RNBAKABNR b - - 0 1",
  },
};

const MIDGAME_DATA: Record<string, TopicData> = {
  mid_tactic: {
    label: "Kỹ thuật Thí Quân",
    prompt:
      "Trong trung cuộc, khi nào nên thực hiện thí quân để tranh tiên? Cho ví dụ lý thuyết.",
    fen: "2bakab2/9/1cn5r/p1p1p1p1p/2P6/9/P1P1P3P/1C2C4/9/RNBAKABNR r - - 0 10",
  },
  mid_attack: {
    label: "Công sát Tướng",
    prompt:
      "Các đội hình công sát phổ biến trong trung cuộc: Xe Pháo Mã kết hợp như thế nào?",
    fen: "3ak4/9/4b4/9/2b6/2R6/9/4N4/4A4/2B1K1B2 r - - 0 1",
  },
};

const ENDGAME_DATA: Record<string, TopicData> = {
  end_rook_pawn: {
    label: "Xe Chốt thắng Sĩ Tượng bền",
    prompt:
      "Làm thế nào để bên Xe Chốt thắng được bên Sĩ Tượng bền? Nguyên tắc 'Bắc cầu' là gì?",
    fen: "4k4/4a4/4b4/9/9/9/P8/4R4/9/4K4 r - - 0 1",
  },
  end_horse_pawn: {
    label: "Mã Chốt (Cờ tàn thực dụng)",
    prompt:
      "Kỹ thuật Mã Chốt phối hợp tấn công. 'Mã ngọa tào' kết hợp Chốt lụt.",
    fen: "4k4/9/9/9/2N6/9/4p4/9/4K4 r - - 0 1",
  },
};

const PUZZLE_DATA: Record<string, TopicData> = {
  puzzle_khong_minh: {
    label: "Khổng Minh Tá Tiễn",
    tag: "Kinh Điển",
    prompt:
      "Phân tích thế cờ 'Khổng Minh Tá Tiễn'. Đâu là bẫy của thế cờ này và cách giải đúng cho bên Đỏ?",
    fen: "3ak4/4a4/4b4/P8/9/2R6/9/9/4A4/2B1K1C2 r - - 0 1",
  },
  puzzle_stalemate: {
    label: "Thập Diện Mai Phục",
    tag: "Khó",
    prompt:
      "Giải thế cờ này: Bên Đỏ đi trước. Mục tiêu là chiếu bí hoặc cầu hòa?",
    fen: "4k4/3P5/3P5/9/9/9/9/9/4p4/4K4 r - - 0 1",
  },
  puzzle_wild: {
    label: "Đơn Tử Cứu Chúa",
    tag: "Cực Khó",
    prompt:
      "Đây là thế cờ 'Đơn tử cứu chúa'. Hãy phân tích nước đi tinh diệu của con Mã đỏ.",
    fen: "2b2k2/4a4/4b4/9/9/6N2/9/9/9/4K3 r - - 0 1",
  },
};

const ALL_TOPICS: Record<string, TopicData> = {
  ...OPENING_DATA,
  ...MIDGAME_DATA,
  ...ENDGAME_DATA,
  ...PUZZLE_DATA,
  history: {
    label: "Lịch sử Cờ Tướng",
    prompt: "Tóm tắt lịch sử cờ tướng.",
    fen: "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C5C1/9/RNBAKABNR r - - 0 1",
  },
  rules: {
    label: "Luật chơi cơ bản",
    prompt: "Luật chơi cờ tướng.",
    fen: "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C5C1/9/RNBAKABNR r - - 0 1",
  },
};

type MenuItem = Required<MenuProps>["items"][number];
function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group",
): MenuItem {
  return { key, icon, children, label, type } as MenuItem;
}

const menuItems: MenuProps["items"] = [
  getItem(
    "Tổng Quan",
    "grp_general",
    null,
    [
      getItem("Lịch sử Cờ Tướng", "history", <HistoryOutlined />),
      getItem("Luật & Cách Đi", "rules", <BookOutlined />),
    ],
    "group",
  ),
  getItem("Khai Cuộc (Opening)", "sub_opening", <ReadOutlined />, [
    getItem("Pháo Đầu", "open_central"),
    getItem("Bình Phong Mã", "open_screen_horse"),
    getItem("Phi Tượng Cuộc", "open_elephant"),
    getItem("Tiên Nhân Chỉ Lộ", "open_pawn"),
  ]),
  getItem("Trung Cuộc (Midgame)", "sub_midgame", <ThunderboltOutlined />, [
    getItem("Chiến thuật Thí Quân", "mid_tactic"),
    getItem("Đội hình Công Sát", "mid_attack"),
  ]),
  getItem("Tàn Cuộc (Endgame)", "sub_endgame", <FlagOutlined />, [
    getItem("Xe Chốt nghệ thuật", "end_rook_pawn"),
    getItem("Mã Chốt thực dụng", "end_horse_pawn"),
  ]),
  getItem("Cờ Thế Giang Hồ", "sub_puzzle", <FireOutlined />, [
    getItem("Khổng Minh Tá Tiễn", "puzzle_khong_minh"),
    getItem("Thập Diện Mai Phục", "puzzle_stalemate"),
    getItem("Đơn Tử Cứu Chúa", "puzzle_wild"),
  ]),
];

// --- COMPONENT CHÍNH ---

const App: React.FC = () => {
  const screens = useBreakpoint();
  const [content, setContent] = useState<string>(
    "# 👋 Chào mừng đến với Kỳ Đạo AI\n\nHãy chọn một thế cờ hoặc bài học từ menu bên trái.\n\nTôi đã chuẩn bị sẵn các thế cờ **Giang Hồ** hiểm hóc và các bài **Khai cuộc** chuẩn mực.",
  );
  const [fen, setFen] = useState<string>(
    "rnbakabnr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C5C1/9/RNBAKABNR r - - 0 1",
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [currentKey, setCurrentKey] = useState<string>("history");
  const [customPrompt, setCustomPrompt] = useState<string>("");

  // State quản lý ẩn/hiện menu (Sider)
  const [collapsed, setCollapsed] = useState(false);

  // Tự động thu gọn menu khi vào mobile, mở ra khi desktop
  useEffect(() => {
    if (!screens.lg) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [screens.lg]);

  const handleAIResponse = async (promptInput: string) => {
    setLoading(true);
    try {
      const raw = await fetchAIChessContent(promptInput);
      if (typeof raw === "string") {
        setContent(raw);
      } else if (raw && typeof raw === "object") {
        const data = raw as { content?: string; fen?: string };
        setContent(data.content ?? "");
        if (data.fen && data.fen.length > 10) setFen(data.fen);
      }
    } catch (err) {
      message.error("Đại sư đang bận suy nghĩ, vui lòng thử lại!");
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    const key = e.key;
    const data = ALL_TOPICS[key as keyof typeof ALL_TOPICS];

    if (data) {
      setCurrentKey(key);
      setFen(data.fen);
      handleAIResponse(data.prompt);
    }

    // Trên mobile, chọn xong thì đóng menu
    if (!screens.lg) {
      setCollapsed(true);
    }
  };

  const handleCustomAsk = () => {
    if (!customPrompt.trim()) return;
    setCurrentKey("custom");
    handleAIResponse(customPrompt);
    setCustomPrompt("");
  };

  // Styles cho Sidebar: Desktop (static) vs Mobile (fixed)
  const siderStyle: React.CSSProperties = !screens.lg
    ? {
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1000, // Cao hơn overlay
        boxShadow: "4px 0 15px rgba(0,0,0,0.1)",
      }
    : {
        background: "#2c1e14",
        boxShadow: "4px 0 15px rgba(0,0,0,0.05)",
        zIndex: 10,
        position: "sticky",
        top: 0,
        left: 0,
      };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Lexend Deca', sans-serif",
          colorPrimary: "#d46b47",
          borderRadius: 12,
          colorBgLayout: "#fdfbf7",
        },
        components: {
          Menu: {
            itemSelectedBg: "#fff0e6",
            itemSelectedColor: "#d46b47",
          },
        },
      }}
    >
      <Layout style={{ minHeight: "100vh" }}>
        {/* --- MOBILE OVERLAY (Blurry Background) --- */}
        {!screens.lg && !collapsed && (
          <div
            className="mobile-overlay"
            onClick={() => setCollapsed(true)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(44, 30, 20, 0.4)", // Dark overlay
              backdropFilter: "blur(6px)", // Hiệu ứng làm mờ
              zIndex: 999, // Nằm dưới Sider (1000) nhưng trên Content
              transition: "opacity 0.3s ease",
            }}
          />
        )}

        {/* --- SIDEBAR --- */}
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth="0" // Ẩn hoàn toàn khi đóng trên mobile
          width={280}
          style={{
            ...siderStyle,
            background: "#2c1e14",
          }}
        >
          {/* Nút đóng cho Mobile bên trong Sider */}
          {!screens.lg && (
            <div
              style={{
                position: "absolute",
                top: 15,
                right: 15,
                color: "rgba(255,255,255,0.5)",
                cursor: "pointer",
              }}
              onClick={() => setCollapsed(true)}
            >
              <CloseOutlined style={{ fontSize: 20 }} />
            </div>
          )}

          <div
            style={{
              padding: "25px 20px",
              textAlign: "center",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #d46b47 0%, #8c5e3c 100%)",
                width: 50,
                height: 50,
                borderRadius: 16,
                margin: "0 auto 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 16px rgba(212, 107, 71, 0.3)",
              }}
            >
              <CrownFilled style={{ fontSize: "24px", color: "white" }} />
            </div>
            <Title level={4} style={{ color: "#fff", margin: 0, fontSize: 18 }}>
              Kỳ Đạo AI
            </Title>
          </div>
          <Menu
            mode="inline"
            selectedKeys={[currentKey]}
            defaultOpenKeys={["sub_opening", "sub_puzzle"]}
            onClick={handleMenuClick}
            items={menuItems}
            theme="dark"
            style={{ background: "transparent", border: "none", marginTop: 5 }}
          />
        </Sider>

        {/* --- MAIN LAYOUT --- */}
        <Layout>
          {/* HEADER */}
          <Header
            style={{
              padding: "0 20px",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #efe8df",
              height: 64,
              position: "sticky",
              top: 0,
              zIndex: 900,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
              {/* Nút Toggle cho Mobile */}
              {!screens.lg && (
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ fontSize: "18px", color: "#4a3b32" }}
                />
              )}

              <div style={{ display: "flex", flexDirection: "column" }}>
                <Title
                  level={5}
                  style={{
                    margin: 0,
                    color: "#4a3b32",
                    fontSize: !screens.md ? "14px" : "16px",
                  }}
                  ellipsis={{ rows: 1 }}
                >
                  {ALL_TOPICS[currentKey as keyof typeof ALL_TOPICS]?.label ||
                    "Hỏi đáp tự do"}
                </Title>
              </div>
              {ALL_TOPICS[currentKey as keyof typeof ALL_TOPICS]?.tag &&
                screens.md && (
                  <Tag color="#d46b47">
                    {ALL_TOPICS[currentKey as keyof typeof ALL_TOPICS]?.tag}
                  </Tag>
                )}
            </div>
          </Header>

          {/* CONTENT */}
          <Content
            style={{
              margin: !screens.md ? "16px" : "24px 32px",
              overflow: "initial",
            }}
          >
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
              <Card
                bordered={false}
                style={{
                  minHeight: "60vh",
                  marginBottom: "24px",
                  boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
                  borderRadius: 24,
                  overflow: "hidden", // Ngăn overflow trong card
                }}
                bodyStyle={{ padding: !screens.md ? "15px" : "24px" }}
              >
                {loading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "80px 0",
                      opacity: 0.7,
                    }}
                  >
                    <Spin
                      size="large"
                      indicator={
                        /* Đã đổi từ RobotOutlined thành LoadingOutlined */
                        <LoadingOutlined style={{ fontSize: 40 }} spin />
                      }
                    />
                    <p style={{ marginTop: 20 }}>Đại sư đang phân tích...</p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: !screens.lg ? "column-reverse" : "row", // Mobile: Bàn cờ lên trên (hoặc dưới tùy ý, ở đây để dưới markdown)
                      gap: "40px",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* CỘT NỘI DUNG */}
                    <div
                      style={{
                        flex: 1,
                        width: "100%", // Đảm bảo không tràn
                        minWidth: 0, // Fix flexbox overflow text
                      }}
                      className="markdown-body"
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{ img: () => null }}
                      >
                        {content}
                      </ReactMarkdown>
                    </div>

                    {/* CỘT BÀN CỜ */}
                    {fen && (
                      <div
                        style={{
                          flexShrink: 0,
                          margin: "0 auto",
                          position: !screens.lg ? "static" : "sticky",
                          top: 20,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            marginBottom: 15,
                            fontWeight: 700,
                            color: "#d46b47",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontSize: "12px",
                          }}
                        >
                          <AppstoreOutlined /> Bàn Cờ Minh Họa
                        </div>
                        {/* Wrapper to scale board on very small screens */}
                        <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                          <XiangqiBoard fen={fen} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* INPUT AREA */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 20,
                  boxShadow: "0 5px 20px -5px rgba(212, 107, 71, 0.15)",
                  border: "1px solid #f0e6dc",
                }}
                bodyStyle={{ padding: "15px 20px" }}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  <div style={{ flex: 1 }}>
                    <Text
                      strong
                      style={{
                        color: "#d46b47",
                        fontSize: "13px",
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      <RobotOutlined /> Hỏi thêm:
                    </Text>
                    <TextArea
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      placeholder="Nhập câu hỏi..."
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      bordered={false}
                      style={{ padding: 0, fontSize: "16px", resize: "none" }}
                      onPressEnter={(e) => {
                        if (!e.shiftKey) {
                          e.preventDefault();
                          handleCustomAsk();
                        }
                      }}
                    />
                  </div>
                  <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={<SendOutlined />}
                    onClick={handleCustomAsk}
                    loading={loading}
                    style={{ marginTop: "auto" }}
                  />
                </div>
              </Card>
            </div>
          </Content>

          <Footer
            style={{
              textAlign: "center",
              background: "transparent",
              color: "#8c7b70",
              fontSize: "12px",
            }}
          >
            Chess AI Academy ©{new Date().getFullYear()}
          </Footer>
        </Layout>
        <FloatButton.BackTop
          type="primary"
          icon={<ArrowUpOutlined />}
          style={{ right: 40, bottom: 40 }}
          visibilityHeight={300}
          tooltip="Lên đầu trang"
        />
      </Layout>
    </ConfigProvider>
  );
};

export default App;
